

import { imageToBase64 } from "./imageToBase64";
import leapLogo from "../../../../../assets/leap-logo.png";
import emergingLeaderImg from "../../../../../assets/archetypes/emerging-leader.png";
import strategicLeaderImg from "../../../../../assets/archetypes/strategic-leader.png";
import visionaryLeaderImg from "../../../../../assets/archetypes/visionary-leader.png";
import type { LearnerReport, LearnerTitle, ScoreTag } from "./Data/reportData";
import type { CompetencyChecklistData } from "./Data/checklistData";
import { checklistPageConfig } from "./Data/checklistData";
import { takeawaysPageConfig, type CompetencyTakeaway } from "./Data/takeawaysData";
import { getTitleFromScore, getCommentary,getTagFromScore  } from "./Data/reportData";


// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shape of the API response from gamereport()
 * Mirrors exactly what each React page reads from reportData
 */
export interface ReportAPIData {
  // ── CoverPage ──────────────────────────────────────────────────
                        // → learnerName display
lenUsername:any;
  // ── PerformancePage ────────────────────────────────────────────
  overallScoreOutOf10: number;               // → ScoreDisplay score
  questScoreOutOf10: Record<number, number>; // → CompetencyCard score per quest (1-based)
  title: LearnerTitle;                       // → TitleBadge  e.g. "Emerging Leader"
  commentary: string;                        // → Performance Summary paragraph

  // ── ChecklistPage ──────────────────────────────────────────────
  questApproachReport: Record<number, {      // → keyed by quest number (1-based)
    interactions: Array<{
      interactionId: string | number;
      checklist: string;                     // → checklist item text
      result: boolean;                       // → checked state
    }>;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS helpers
// ─────────────────────────────────────────────────────────────────────────────

function collectAllCSS(): string {
  const rules: string[] = [];
  try {
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        for (const rule of Array.from(sheet.cssRules)) {
          rules.push(rule.cssText);
        }
      } catch {
        // cross-origin stylesheet – skip
      }
    }
  } catch { /* no stylesheets */ }
  return rules.join("\n");
}

const pdfStyles = `
  @page { size: 1600px 900px; margin: 0; }
  html, body { margin: 0; padding: 0; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  *, *::before, *::after { animation: none !important; transition: none !important; }
  .pdf-mode .pdf-page {
    width: 1600px; height: 900px; overflow: hidden; position: relative;
    break-after: page; page-break-after: always;
  }
  .pdf-mode .pdf-page:last-child { break-after: auto; page-break-after: auto; }
  .pdf-mode .report-page {
    width: 1600px !important; height: 900px !important;

  }
  .pdf-mode * { backdrop-filter: none !important; -webkit-backdrop-filter: none !important; }
  .pdf-mode .grid-cols-1.lg\\:grid-cols-12 { grid-template-columns: repeat(12, minmax(0, 1fr)); }
  .pdf-mode .lg\\:col-span-5 { grid-column: span 5 / span 5; }
  .pdf-mode .lg\\:col-span-7 { grid-column: span 7 / span 7; }
  .pdf-mode .lg\\:gap-10 { gap: 2.5rem; }
  .pdf-mode .grid-cols-1.lg\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .pdf-mode .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .pdf-mode .md\\:p-16 { padding: 4rem; }
  .pdf-mode .checklist-bg .gap-5 { gap: 1rem; }
  .pdf-mode .checklist-bg .p-5 { padding: 1rem; }
  .pdf-mode .checklist-bg .mb-6 { margin-bottom: 1.25rem; }
  .pdf-mode .checklist-bg .gap-3 { gap: 0.5rem; }
  .pdf-mode .cover-bg {
    background: linear-gradient(180deg, hsl(220,30%,4%) 0%, hsl(220,35%,7%) 100%) !important;
  }
  .pdf-mode .analytics-bg {
    background: linear-gradient( 135deg, hsl(220 30% 4%) 0%, hsl(220 35% 7%) 50%, hsl(220 30% 4%) 100% );
  }
  .pdf-mode .checklist-bg {
    background: linear-gradient( 160deg, hsl(220 35% 6%) 0%, hsl(220 30% 6%) 50%, hsl(220 30% 4%) 100% ); position: relative;
  }
  .pdf-mode .takeaways-bg {
    background: linear-gradient( 145deg, hsl(220 30% 4%) 0%, hsl(225 35% 8%) 60%, hsl(220 35% 6%) 100% );
  }
  .pdf-mode .cover-bg > .absolute.inset-0.pointer-events-none { display: none !important; }
  .pdf-mode .cover-bg::before {
    content: ''; position: absolute; inset: 0;
    background:
      radial-gradient(ellipse at 20% 30%, hsla(260,70%,60%,0.25) 0%, transparent 50%),
      radial-gradient(ellipse at 75% 60%, hsla(190,100%,60%,0.2) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, hsla(190,100%,60%,0.15) 0%, transparent 60%);
    pointer-events: none;
  }
  .pdf-mode .sm\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Inline SVG icons  (same competencies as CompetencyIcons.tsx)
// ─────────────────────────────────────────────────────────────────────────────

const goalSettingSVG = `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="goalGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#06B6D4"/><stop offset="100%" stop-color="#A855F7"/></linearGradient></defs><path d="M21.92,5.62A1,1,0,0,0,21,5H19V3a1,1,0,0,0-.62-.92,1,1,0,0,0-1.09.21l-3,3A1,1,0,0,0,14,6V8.59l-2.21,2.2a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L15.41,10H18a1,1,0,0,0,.71-.29l3-3A1,1,0,0,0,21.92,5.62Z" fill="url(#goalGrad)"/><path d="M12,22A10,10,0,0,1,12,2h.42a1,1,0,1,1-.18,2H12a8,8,0,1,0,8,8,1.93,1.93,0,0,0,0-.24,1,1,0,0,1,.91-1.09,1,1,0,0,1,1.09.91c0,.14,0,.28,0,.42A10,10,0,0,1,12,22Zm5.88-8.8a1,1,0,0,0-2-.4A4,4,0,1,1,11.2,8.08a1,1,0,1,0-.4-2,6,6,0,1,0,7.08,7.08Z" fill="url(#goalGrad)"/></svg>`;
const delegationSVG = `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="delegGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#06B6D4"/><stop offset="100%" stop-color="#A855F7"/></linearGradient></defs><g transform="rotate(90,12,12)"><path fill-rule="evenodd" clip-rule="evenodd" d="M13.803 5.33333C13.803 3.49238 15.3022 2 17.1515 2C19.0008 2 20.5 3.49238 20.5 5.33333C20.5 7.17428 19.0008 8.66667 17.1515 8.66667C16.2177 8.66667 15.3738 8.28596 14.7671 7.67347L10.1317 10.8295C10.1745 11.0425 10.197 11.2625 10.197 11.4872C10.197 11.9322 10.109 12.3576 9.94959 12.7464L15.0323 16.0858C15.6092 15.6161 16.3473 15.3333 17.1515 15.3333C19.0008 15.3333 20.5 16.8257 20.5 18.6667C20.5 20.5076 19.0008 22 17.1515 22C15.3022 22 13.803 20.5076 13.803 18.6667C13.803 18.1845 13.9062 17.7255 14.0917 17.3111L9.05007 13.9987C8.46196 14.5098 7.6916 14.8205 6.84848 14.8205C4.99917 14.8205 3.5 13.3281 3.5 11.4872C3.5 9.64623 4.99917 8.15385 6.84848 8.15385C7.9119 8.15385 8.85853 8.64725 9.47145 9.41518L13.9639 6.35642C13.8594 6.03359 13.803 5.6896 13.803 5.33333Z" fill="url(#delegGrad)"/></g></svg>`;
const givingFeedbackSVG = `<svg width="32" height="32" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="feedbackGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#06B6D4"/><stop offset="100%" stop-color="#A855F7"/></linearGradient></defs><path d="m4.75 10.75h-3m12.5-2c0 3-2.79822 5.5-6.25 5.5-3.75 0-6.25-3.5-6.25-3.5v3.5m9.5-9h3m-12.5 2c0-3 2.79822-5.5 6.25-5.5 3.75 0 6.25 3.5 6.25 3.5v-3.5" stroke="url(#feedbackGrad)" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/></svg>`;
const conflictManagementSVG = `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="conflictGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#06B6D4"/><stop offset="100%" stop-color="#A855F7"/></linearGradient></defs><path d="M3 11.9914C3 17.6294 7.23896 20.3655 9.89856 21.5273C10.62 21.8424 10.9807 22 12 22V8L3 11V11.9914Z" fill="url(#conflictGrad)"/><path opacity="0.85" d="M14.1014 21.5273C16.761 20.3655 21 17.6294 21 11.9914V11L12 8V22C13.0193 22 13.38 21.8424 14.1014 21.5273Z" fill="url(#conflictGrad)"/><path opacity="0.85" d="M8.83772 2.80472L8.26491 3.00079C5.25832 4.02996 3.75503 4.54454 3.37752 5.08241C3 5.62028 3 7.21907 3 10.4167V11L12 8V2C11.1886 2 10.405 2.26824 8.83772 2.80472Z" fill="url(#conflictGrad)"/><path d="M15.7351 3.00079L15.1623 2.80472C13.595 2.26824 12.8114 2 12 2V8L21 11V10.4167C21 7.21907 21 5.62028 20.6225 5.08241C20.245 4.54454 18.7417 4.02996 15.7351 3.00079Z" fill="url(#conflictGrad)"/></svg>`;

const competencySVGs: Record<string, string> = {
  "Goal Setting": goalSettingSVG,
  "Delegation": delegationSVG,
  "Giving Feedback": givingFeedbackSVG,
  "Conflict Management": conflictManagementSVG,
};

// Lucide-equivalent inline SVGs
const circleCheckSVG = (color: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>`;
const lightbulbSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="hsl(42,90%,55%)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>`;
const zapSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="hsl(190,95%,55%)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/></svg>`;
const circleHelpSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="hsl(260,60%,55%)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>`;

// ─────────────────────────────────────────────────────────────────────────────
// Circular progress SVG
// ─────────────────────────────────────────────────────────────────────────────

function circularProgressSVG(score: number, size: "sm" | "md" | "lg"): string {
  const percentage = (score / 10) * 100;
  const cfg = {
    sm: { dimension: 48, stroke: 4, fontSize: "14px" },
    md: { dimension: 100, stroke: 6, fontSize: "24px" },
    lg: { dimension: 140, stroke: 8, fontSize: "36px" },
  }[size];
  const radius = (cfg.dimension - cfg.stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (percentage / 100) * circumference;
  const gradId = `pg-${size}`;
  const scoreText = score % 1 === 0 ? String(score) : score.toFixed(1);

  return `
    <div style="position:relative;width:${cfg.dimension}px;height:${cfg.dimension}px;">
      <div style="position:absolute;inset:0;border-radius:9999px;background:hsla(190,95%,55%,0.2);filter:blur(8px);opacity:0.5;transform:scale(1.1);"></div>
      <svg width="${cfg.dimension}" height="${cfg.dimension}" style="position:relative;transform:rotate(-90deg);">
        <defs><linearGradient id="${gradId}" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="hsl(175,80%,42%)"/>
          <stop offset="100%" stop-color="hsl(190,95%,55%)"/>
        </linearGradient></defs>
        <circle cx="${cfg.dimension / 2}" cy="${cfg.dimension / 2}" r="${radius}" fill="none" stroke="hsla(215,20%,14%,0.3)" stroke-width="${cfg.stroke}"/>
        <circle cx="${cfg.dimension / 2}" cy="${cfg.dimension / 2}" r="${radius}" fill="none" stroke="url(#${gradId})" stroke-width="${cfg.stroke}" stroke-linecap="round" stroke-dasharray="${circumference}" stroke-dashoffset="${dashOffset}"/>
      </svg>
      <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;">
        <span style="font-size:${cfg.fontSize};font-weight:300;color:hsl(200,20%,95%);">${scoreText}</span>
      </div>
    </div>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Accent / color maps  (same as React pages)
// ─────────────────────────────────────────────────────────────────────────────

const tagColorMap: Record<ScoreTag, string> = {
  Fair: "hsl(210,20%,65%)",
  Skilled: "#A5F3FC",
  Pro: "#22D3EE",
};

const competencyAccents: Record<string, string> = {
  "Goal Setting": "cyan",
  "Delegation": "teal",
  "Giving Feedback": "purple",
  "Conflict Management": "gold",
};

const accentBorderColors: Record<string, string> = {
  cyan: "hsl(190,95%,55%)",
  teal: "hsl(175,80%,42%)",
  purple: "hsl(260,60%,55%)",
  gold: "hsl(300,70%,55%)",
};

const accentDividerColors: Record<string, string> = {
  cyan: "hsla(190,95%,55%,0.2)",
  teal: "hsla(175,80%,42%,0.2)",
  purple: "hsla(260,60%,55%,0.2)",
  gold: "hsla(300,70%,55%,0.2)",
};

const accentGradientOverlay: Record<string, string> = {
  cyan: "hsla(190,95%,55%,0.15)",
  teal: "hsla(175,80%,42%,0.15)",
  purple: "hsla(260,60%,55%,0.15)",
  gold: "hsla(300,70%,55%,0.15)",
};

const checklistItemColors: Record<string, { border: string; icon: string }> = {
  cyan: { border: "hsla(190,95%,55%,0.25)", icon: "hsl(190,95%,55%)" },
  teal: { border: "hsla(172,66%,50%,0.25)", icon: "hsl(172,66%,50%)" },
  purple: { border: "hsla(280,85%,65%,0.25)", icon: "hsl(280,85%,65%)" },
  gold: { border: "hsla(300,70%,55%,0.25)", icon: "hsl(300,70%,55%)" },
};

const contextualHeaders: Record<string, string> = {
  "Goal Setting": "When setting goals:",
  "Delegation": "When delegating:",
  "Giving Feedback": "When giving feedback:",
  "Conflict Management": "When navigating conflict:",
};

// TitleBadge helpers
const archetypeImgUrls: Record<LearnerTitle, string> = {
  "Emerging People Leader": emergingLeaderImg,
  "Effective People Leader": strategicLeaderImg,
  "Impact‑Driven People Leader": visionaryLeaderImg,
};

const frameBorders: Record<LearnerTitle, string> = {
  "Emerging People Leader": "hsla(260,60%,55%,0.3)",
  "Effective People Leader": "hsla(260,60%,55%,0.4)",
  "Impact‑Driven People Leader": "hsla(263,70%,65%,0.5)",
};

const dividerColors: Record<LearnerTitle, string> = {
  "Emerging People Leader": "rgba(31,228,208,0.4)",
  "Effective People Leader": "rgba(31,228,208,0.4)",
  "Impact‑Driven People Leader": "rgba(107,255,207,0.4)",
};

// Takeaway accents (same as TakeawaysPage.tsx)
const takeawayAccents: Record<string, { gradient: string; iconBg: string }> = {
  "Goal Setting": {
    gradient: "linear-gradient(to bottom, hsla(190,95%,55%,0.2), transparent, transparent)",
    iconBg: "background:linear-gradient(to bottom right,rgb(15,23,42),black);border-color:hsla(190,95%,55%,0.2);box-shadow:inset 0 0 12px hsla(190,95%,55%,0.15);",
  },
  "Delegation": {
    gradient: "linear-gradient(to bottom, hsla(175,80%,42%,0.2), transparent, transparent)",
    iconBg: "background:linear-gradient(to bottom right,rgb(15,23,42),black);border-color:hsla(175,80%,42%,0.2);box-shadow:inset 0 0 12px hsla(175,80%,42%,0.15);",
  },
  "Giving Feedback": {
    gradient: "linear-gradient(to bottom, hsla(260,60%,55%,0.2), transparent, transparent)",
    iconBg: "background:linear-gradient(to bottom right,rgb(15,23,42),black);border-color:hsla(260,60%,55%,0.2);box-shadow:inset 0 0 12px hsla(260,60%,55%,0.15);",
  },
  "Conflict Management": {
    gradient: "linear-gradient(to bottom, hsla(320,70%,55%,0.2), transparent, transparent)",
    iconBg: "background:linear-gradient(to bottom right,rgb(15,23,42),black);border-color:hsla(320,70%,55%,0.2);box-shadow:inset 0 0 12px hsla(320,70%,55%,0.15);",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 1 — Cover
// Mirrors CoverPage.tsx → uses: reportData.nickName
// ─────────────────────────────────────────────────────────────────────────────

function buildCoverPage(lenUsername: string, logoB64: string): string {
  return `
  <div class="pdf-page" style="min-height:768px;">
    <div class="report-page cover-bg" style="display:flex;align-items:center;justify-content:center;padding:4rem;overflow:hidden;height:100%;position:relative;">
      <div class="absolute inset-0 pointer-events-none"></div>
      <div style="position:relative;z-index:10;text-align:center;max-width:56rem;margin:0 auto;">

        <!-- Logo -->
        <div style="position:relative;width:176px;height:176px;margin:0 auto 3.5rem;">
          <div style="position:absolute;inset:-24px;border-radius:9999px;border:2px dashed hsla(190,95%,55%,0.3);"></div>
          <div style="position:absolute;inset:-12px;border-radius:9999px;border:1px solid hsla(190,95%,55%,0.4);"></div>
          <div style="position:absolute;inset:-4px;border-radius:9999px;border:1px solid hsla(260,60%,55%,0.5);"></div>
          <div style="position:absolute;inset:0;border-radius:9999px;background:hsl(220,30%,4%);"></div>
          <div style="position:absolute;inset:0;border-radius:9999px;background:linear-gradient(to bottom right,hsla(260,60%,55%,0.1),transparent,hsla(190,95%,55%,0.1));"></div>
          <img src="${logoB64}" alt="ECHO Logo" style="position:relative;width:100%;height:100%;object-fit:contain;padding:1rem;border-radius:50%"/>
        </div>

        <!-- LEAP title -->
        <h1 style="font-size:8rem;font-weight:400;letter-spacing:-0.025em;margin-bottom:1.5rem;line-height:1;">
          <span style="background:linear-gradient(to right,hsl(190,95%,55%),hsl(175,80%,42%),hsl(190,95%,55%));-webkit-background-clip:text;-webkit-text-fill-color:transparent;text-shadow:0 0 40px hsla(190,95%,55%,0.5),0 0 80px hsla(190,95%,55%,0.3);">
            ECHO
          </span>
        </h1>

        <!-- Accent line -->
        <div style="width:10rem;height:2px;margin:0 auto 2rem;background:linear-gradient(to right,transparent,hsl(190,95%,55%),transparent);"></div>

        <!-- Learner name — uses nickName exactly like CoverPage.tsx -->
        <h2 style="font-size:1.875rem;font-weight:500;letter-spacing:0.3em;text-transform:uppercase;color:hsla(200,20%,95%,0.9);margin-bottom:3.5rem;">
          ${lenUsername}'s Performance
        </h2>

        <p style="font-size:1.25rem;color:hsla(200,20%,95%,0.7);font-weight:500;max-width:32rem;margin:0 auto 4rem;line-height:1.625;">
          Insights from your First Time People Leadership Journey
        </p>

        <div style="display:inline-block;position:relative;">
          <span style="display:inline-block;padding:1.1rem 2.1rem;font-size:1rem;letter-spacing:0.25em;text-transform:uppercase;color:hsl(190,95%,55%);border:1px solid hsla(190,95%,55%,0.4);border-radius:9999px;background:hsla(215,25%,10%,0.5);">
            Your Personalised Report
          </span>
        </div>

        <!-- Corner decorations -->
        <div style="position:absolute;top:1rem;left:1rem;width:4rem;height:4rem;border-left:2px solid hsla(190,95%,55%,0.3);border-top:2px solid hsla(190,95%,55%,0.3);border-top-left-radius:0.5rem;"></div>
        <div style="position:absolute;top:1rem;right:1rem;width:4rem;height:4rem;border-right:2px solid hsla(190,95%,55%,0.3);border-top:2px solid hsla(190,95%,55%,0.3);border-top-right-radius:0.5rem;"></div>
        <div style="position:absolute;bottom:3rem;left:1rem;width:4rem;height:4rem;border-left:2px solid hsla(190,95%,55%,0.3);border-bottom:2px solid hsla(190,95%,55%,0.3);border-bottom-left-radius:0.5rem;"></div>
        <div style="position:absolute;bottom:3rem;right:1rem;width:4rem;height:4rem;border-right:2px solid hsla(190,95%,55%,0.3);border-bottom:2px solid hsla(190,95%,55%,0.3);border-bottom-right-radius:0.5rem;"></div>
      </div>
    </div>
  </div>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 2 — Performance
// Mirrors PerformancePage.tsx →
//   nickName           = reportData.nickName
//   overallScore       = reportData.overallScoreOutOf10
//   questScoreOutOf10  = reportData.questScoreOutOf10[questNo]  (1-based)
//   title              = reportData.title
//   commentary         = reportData.commentary
//   static competencies list for names/tags/icons from LearnerReport
// ─────────────────────────────────────────────────────────────────────────────

function buildPerformancePage(
  apiData: ReportAPIData,
  staticData: LearnerReport,
  archetypeB64: string,
): string {
  // Exact same resolution logic as PerformancePage.tsx
  // const learnerName = apiData.lenUsername;
  // const overallScore = apiData.overallScoreOutOf10 ?? staticData.overallScore;
  // const questScores = apiData.questScoreOutOf10 || {};
  // const title = apiData.title || staticData.title;
  // const commentary = apiData.commentary || staticData.commentary;

  const learnerName = apiData?.lenUsername;

const overallScore =
  apiData?.overallScoreOutOf10 ?? staticData.overallScore;

const questScores = apiData?.questScoreOutOf10 || {};

const title =
  overallScore !== undefined
    ? getTitleFromScore(overallScore)
    : staticData.title;

const commentary =
  overallScore !== undefined
    ? getCommentary(apiData, overallScore)
    : staticData.commentary;

  const competencyCardsHTML = staticData.competencies
    .map((comp, index) => {
      const questNo = index + 1;                              // 1-based, same as React page
      const dynamicScore = questScores[questNo] ?? comp.score;     // ✅ backend score with static fallback
    const dynamicTag = getTagFromScore(dynamicScore);
    const tagColor = tagColorMap[dynamicTag];

      return `
      <div style="padding:1rem;border-radius:0.75rem;background:hsla(215,25%,10%,0.7);border:1px solid hsl(210,25%,20%);box-shadow:0 0 0 1px hsla(190,95%,55%,0.1),0 4px 20px -4px hsla(190,95%,55%,0.15),inset 0 1px 0 hsla(200,20%,95%,0.05);">
        <div style="display:flex;align-items:center;gap:1rem;">
          <div style="width:3.5rem;height:3.5rem;border-radius:0.5rem;background:linear-gradient(to bottom right,rgb(15,23,42),black);display:flex;align-items:center;justify-content:center;border:1px solid hsla(190,95%,55%,0.2);">
            ${competencySVGs[comp.name] || ""}
          </div>
          <div style="flex:1;display:flex;flex-direction:column;gap:0.25rem;">
            <span style="font-size:1rem;font-weight:500;color:hsl(200,20%,95%);">${comp.name}</span>
          
            <span style="font-size:0.75rem;font-weight:500;text-transform:uppercase;letter-spacing:0.2em;color:${tagColor};">${dynamicTag}</span>
          </div>
          ${circularProgressSVG(dynamicScore, "sm")}
        </div>
      </div>`;
    })
    .join("");

  return `
  <div class="pdf-page" style="min-height:768px;">
    <div class="report-page analytics-bg" style="padding:4rem;overflow:hidden;height:100%;display:flex;flex-direction:column;position:relative;">

      <!-- BG glows -->
      <div style="position:absolute;top:2.2%;left:0%;right:0%;bottom:0%;pointer-events:none;">
        <div style="position:absolute;top:0;right:0;width:500px;height:500px;border-radius:9999px;background:radial-gradient(circle,hsla(190,95%,55%,0.08) 0%,transparent 60%);"></div>
        <div style="position:absolute;bottom:0;left:0;width:400px;height:400px;border-radius:9999px;background:radial-gradient(circle,hsla(175,80%,42%,0.06) 0%,transparent 60%);"></div>
        <div style="position:absolute;top:8%;left:0;width:35%;height:1px;background:linear-gradient(to right,transparent,hsla(190,95%,55%,0.1));"></div>
        <div style="position:absolute;top:8%;right:0;width:35%;height:1px;background:linear-gradient(to left,transparent,hsla(190,95%,55%,0.1));"></div>
      </div>

      <div style="position:relative;z-index:10;height:100%;display:flex;flex-direction:column;">

        <!-- Header — uses nickName (same as PerformancePage.tsx) -->
        <div style="text-align:center;margin-bottom:2.5rem;">
          <p style="font-size:1rem;letter-spacing:0.3em;text-transform:uppercase;color:hsl(190,95%,55%);margin-bottom:0.75rem;">Performance Dashboard</p>
          <h2 style="font-size:3.2rem;line-height:1;font-weight:400;color:hsl(200,20%,95%);letter-spacing:0.06em;">${learnerName}</h2>
          <div style="width:4rem;height:2px;margin:1.25rem auto 0;background:linear-gradient(to right,transparent,hsl(190,95%,55%),transparent);"></div>
        </div>

        <!-- Main 12-col grid -->
        <div style="flex:1;display:grid;grid-template-columns:repeat(12,minmax(0,1fr));gap:2.5rem;">

          <!-- LEFT col-span-5 -->
          <div style="grid-column:span 5/span 5;display:flex;flex-direction:column;align-items:center;justify-content:space-between;gap:1.5rem;">

            <!-- TitleBadge — uses reportData.title + archetype image -->
            <div style="width:100%;padding:1.5rem;border-radius:1rem;background:hsla(215,25%,10%,0.6);border:1px solid hsl(210,25%,20%);box-shadow:0 0 0 1px hsla(190,95%,55%,0.1),inset 0 1px 0 hsla(200,20%,95%,0.05);">
              <div style="display:flex;flex-direction:column;align-items:center;gap:1rem;">
                <div style="width:8rem;height:8rem;border-radius:9999px;overflow:hidden;border:2px solid ${frameBorders[title] || frameBorders["Emerging People Leader"]};box-shadow:0 10px 15px -3px rgba(0,0,0,0.3);">
                  <img src="${archetypeB64}" alt="${title}" style="width:100%;height:100%;object-fit:cover;object-position:center top;"/>
                </div>
                <div style="display:flex;flex-direction:column;align-items:center;">
                  <h2 style="font-size:1.27rem;font-weight:700;letter-spacing:0.26em;text-transform:uppercase;background:linear-gradient(to right,#2FB9FF,#6BFFCF);-webkit-background-clip:text;-webkit-text-fill-color:transparent;">${title}</h2>
                  <div style="width:3rem;height:2px;margin-top:0.5rem;border-radius:9999px;background:linear-gradient(to right,transparent,${dividerColors[title] || dividerColors["Emerging People Leader"]},transparent);"></div>
                </div>
              </div>
            </div>

            <!-- Overall score — uses reportData.overallScoreOutOf10 -->
            <div style="width:100%;padding:1.5rem;border-radius:1rem;background:hsla(215,25%,10%,0.6);border:1px solid hsl(210,25%,20%);box-shadow:0 0 0 1px hsla(190,95%,55%,0.1),inset 0 1px 0 hsla(200,20%,95%,0.05);">
              <p style="font-size:0.77rem;color:hsl(190,95%,55%);text-transform:uppercase;letter-spacing:0.2em;text-align:center;margin-bottom:1rem;">Overall Performance</p>
              <div style="display:flex;flex-direction:column;align-items:center;gap:0.75rem;font-weight:500;width:100%;">
                ${circularProgressSVG(overallScore, "lg")}
              </div>
            </div>
          </div>

          <!-- RIGHT col-span-7 -->
          <div style="grid-column:span 7/span 7;display:flex;flex-direction:column;justify-content:flex-start;gap:1.5rem;">

            <!-- Performance Summary — uses reportData.commentary -->
            <div style="padding:1.25rem;border-radius:0.75rem;background:linear-gradient(to bottom right,hsla(215,25%,10%,0.8),hsla(215,25%,10%,0.4));border:1px solid hsla(210,25%,20%,0.8);display:flex;flex-direction:column;justify-content:center;">
              <div style="display:flex;align-items:center;gap:0.5rem;margin-bottom:0.75rem;">
                <div style="width:0.5rem;height:0.5rem;border-radius:9999px;background:hsl(190,95%,55%);"></div>
                <span style="font-size:0.77rem;text-transform:uppercase;letter-spacing:0.1em;color:hsl(200,20%,95%);">Performance Summary</span>
              </div>
              <p style="font-size:1rem;color:hsl(210,20%,65%);line-height:1.625;">${commentary}</p>
            </div>

            <!-- Heading -->
            <div style="padding:0 0.5rem;margin:auto 0;">
              <h3 style="font-size:1.27rem;font-weight:500;color:hsl(200,20%,95%);">Competency Analytics</h3>
              <p style="font-size:1rem;color:hsl(210,20%,65%);margin-top:0.25rem;">Performance breakdown by skill area</p>
            </div>

            <!-- Competency cards — scores from questScoreOutOf10 (1-based) -->
            <div style="margin-top:auto;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem;">
              ${competencyCardsHTML}
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div style="margin-top:auto;padding-top:0.75rem;font-size:1rem;color:hsl(210,20%,65%);font-style:italic;">
          Rating Range: 0-10, Based on Final Score
        </div>
      </div>
    </div>
  </div>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 3 — Checklist
// Mirrors ChecklistPage.tsx →
//   interactions = reportData.questApproachReport[questNo].interactions  (1-based)
//   Falls back to static checklistData items if no interactions
// ─────────────────────────────────────────────────────────────────────────────

function buildChecklistPage(
  apiData: ReportAPIData,
  checklistData: Record<string, CompetencyChecklistData>,
): string {

  // 🔹 Find quests that actually have data
  const questKeys =
    Object.keys(apiData?.questApproachReport || {})
      .map(Number)
      .filter(
        (q) => apiData?.questApproachReport?.[q]?.interactions?.length > 0
      );

  // 🔹 Map competency → quest number
  const checklistEntries = Object.entries(checklistData)
    .map(([competency, compData], index) => ({
      competency,
      compData,
      questNumber: index + 1,
    }))
    .filter((entry) => questKeys.includes(entry.questNumber));

  const cardsHTML = checklistEntries
    .map(({ competency, compData, questNumber }, index) => {

      const accent = competencyAccents[competency] || "cyan";
      const borderColor = accentBorderColors[accent];
      const dividerColor = accentDividerColors[accent];
      const header = contextualHeaders[competency] || competency;
      const itemColors =
        checklistItemColors[accent] || checklistItemColors.cyan;

      const questInteractions =
        apiData?.questApproachReport?.[questNumber]?.interactions || [];

      const topItems = questInteractions.slice(0, 3).map((interaction, i) => ({
        id: `${questNumber}-${interaction.interactionId}`,
        text: interaction.checklist,
        priority: i + 1,
        checked: interaction.result,
      }));

      const itemsHTML = topItems
        .map(
          (item) => `
          <div style="position:relative;padding:0.5rem 1rem 0.5rem 2.5rem;border-radius:0.75rem;background:linear-gradient(135deg,hsla(240,50%,13%,0.95) 0%,hsla(270,40%,16%,0.9) 50%,hsla(220,35%,13%,0.95) 100%);border:1px solid ${itemColors.border};">
            <div style="position:absolute;left:0.75rem;top:50%;transform:translateY(-50%);opacity:0.8;">
              ${circleCheckSVG(itemColors.icon)}
            </div>
            <span style="font-size:1rem;color:hsla(200,20%,95%,0.9);line-height:1.625;display:block;">
              ${item.text}
            </span>
          </div>`
        )
        .join("");

      // 🔹 If odd cards → last card full width
      const isLastOdd =
        checklistEntries.length % 2 !== 0 &&
        index === checklistEntries.length - 1;

      return `
      <div style="
        grid-column:${isLastOdd ? "span 2" : "auto"};
        position:relative;
        padding:1.25rem;
        border-radius:1rem;
        background:hsla(215,25%,10%,0.6);
        border:1px solid hsl(210,25%,20%);
        border-left:2px solid ${borderColor};
        display:flex;
        flex-direction:column;
        overflow:hidden;
        box-shadow:0 0 0 1px hsla(190,95%,55%,0.1),0 4px 20px -4px hsla(190,95%,55%,0.15);
      ">

        <div style="position:absolute;top:0;left:0;right:0;height:5rem;background:linear-gradient(to bottom,${accentGradientOverlay[accent]},transparent);pointer-events:none;border-radius:0.75rem 0.75rem 0 0;"></div>

        <!-- Header row -->
        <div style="display:flex;align-items:center;gap:0.5rem;margin-bottom:0.75rem;position:relative;">
          <div style="width:2rem;height:2rem;border-radius:0.75rem;background:linear-gradient(to bottom right,rgb(15,23,42),black);display:flex;align-items:center;justify-content:center;border:1px solid hsla(190,95%,55%,0.2);">
            ${competencySVGs[competency] || ""}
          </div>
          <h3 style="font-size:1.25rem;font-weight:500;color:hsl(200,20%,95%);">
            ${header}
          </h3>
        </div>

        <!-- Divider -->
        <div style="height:1px;margin-bottom:0.5rem;background:linear-gradient(to right,transparent,${dividerColor},transparent);"></div>

        <!-- Items -->
        <div style="display:flex;flex-direction:column;gap:0.5rem;padding-left:0.5rem;">
          ${itemsHTML}
        </div>
      </div>`;
    })
    .join("");

  return `
  <div class="pdf-page" style="min-height:768px;">
    <div class="report-page checklist-bg" style="padding:4rem;height:100%;display:flex;flex-direction:column;">

      <!-- Header -->
      <div style="text-align:center;margin-bottom:1.5rem;">
        <p style="font-size:1rem;letter-spacing:0.3em;text-transform:uppercase;color:hsl(175,80%,42%);margin-bottom:0.75rem;">
          ${checklistPageConfig.sectionLabel}
        </p>

     <h2 style="font-size:2.28rem;line-height:2.5rem;font-weight:500;letter-spacing:0.05em;">
   <span style="background:linear-gradient(to right,hsl(200,20%,95%),hsl(200,20%,95%),hsl(210,20%,65%));-webkit-background-clip:text;-webkit-text-fill-color:transparent;">
    ${checklistPageConfig.title}
  </span>
 </h2>

        <p style="color:hsl(210,20%,65%);margin-top:0.75rem;font-size:1rem;">
          ${checklistPageConfig.subtitle}
        </p>
      </div>

      <!-- Cards -->
      <div style="
        flex:1;
        display:grid;
        grid-template-columns:repeat(2,minmax(0,1fr));
        gap:1rem;
      ">
        ${cardsHTML}
      </div>

    </div>
  </div>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGES 4 & 5 — Takeaways
// Mirrors TakeawaysPage.tsx →
//   coreIdea / keyMoves / guidingQuestion are FULLY STATIC from takeawaysData
//   pageNumber 1 → entries 0-1,  pageNumber 2 → entries 2-3
// ─────────────────────────────────────────────────────────────────────────────

function buildTakeawayPage(
  entries: [string, CompetencyTakeaway][],
): string {
  const cardsHTML = entries
    .map(([competency, takeaway]) => {
      const accent = takeawayAccents[competency] || takeawayAccents["Goal Setting"];

      const keyMovesHTML = takeaway.keyMoves
        .map(
          (move: any, i: any) => `
          <div style="display:flex;align-items:flex-start;gap:0.75rem;padding-left:0.25rem;">
            <span style="font-size:0.77rem;color:hsl(210,20%,65%);margin-top:0.25rem;font-family:monospace;width:1rem;flex-shrink:0;">${String(i + 1).padStart(2, "0")}</span>
            <p style="font-size:1rem;color:hsla(200,20%,95%,0.85);line-height:1.675;">${move}</p>
          </div>`,
        )
        .join("");

      return `
      <div style="position:relative;padding:1rem 1.5rem ;border-radius:1rem;background:hsla(215,25%,10%,0.6);border:1px solid hsl(210,25%,20%);overflow:hidden;display:flex;flex-direction:column;">
        <div style="position:absolute;top:0;left:0;right:0;height:8rem;background:${accent.gradient};pointer-events:none;"></div>

        <!-- Header -->
        <div style="position:relative;display:flex;align-items:center;gap:0.75rem;margin-bottom:1.25rem;">
          <div style="width:2.5rem;height:2.5rem;border-radius:0.75rem;border-width:1px;border-style:solid;${accent.iconBg};display:flex;align-items:center;justify-content:center;">
            ${competencySVGs[competency] || ""}
          </div>
          <h3 style="font-size:1.25rem;font-weight:500;color:hsl(200,20%,95%);">${competency}</h3>
        </div>

        <!-- Core Idea (static) -->
        <div style="position:relative;margin-bottom:1.25rem;">
          <div style="padding:1rem;border-radius:0.75rem;background:hsla(215,20%,14%,0.4);border:1px solid hsla(210,25%,20%,0.5);">
            <div style="display:flex;align-items:center;gap:0.5rem;margin-bottom:0.5rem;">
              ${lightbulbSVG}
              <span style="font-size:0.78rem;letter-spacing:0.2em;text-transform:uppercase;color:hsl(210,20%,65%);font-weight:500;">Core Idea</span>
            </div>
            <p style="font-size:1rem;color:hsla(200,20%,95%,0.9);font-style:italic;line-height:1.625;padding-left:1.375rem;">${takeaway.coreIdea}</p>
          </div>
        </div>

        <!-- Key Moves (static) -->
        <div style="position:relative;margin-bottom:1.25rem;display:flex;flex-direction:column;">
          <div style="display:flex;align-items:center;gap:0.5rem;margin-bottom:0.75rem;">
            ${zapSVG}
            <span style="font-size:0.78rem;letter-spacing:0.2em;text-transform:uppercase;color:hsl(210,20%,65%);font-weight:500;">Key Moves</span>
          </div>
          <div style="display:flex;flex-direction:column;gap:0.5rem;">${keyMovesHTML}</div>
        </div>

        <!-- Guiding Question (static) -->
        <div style="position:relative;">
          <div style="padding:0.75rem;border-radius:0.5rem;border:1px dashed hsla(210,25%,20%,0.6);background:hsla(200,20%,95%,0.02);">
            <div style="display:flex;align-items:center;gap:0.5rem;margin-bottom:0.5rem;">
              ${circleHelpSVG}
              <span style="font-size:0.78rem;letter-spacing:0.2em;text-transform:uppercase;color:hsl(210,20%,65%);font-weight:500;">Guiding Question</span>
            </div>
            <p style="font-size:1rem;color:hsl(210,20%,65%);font-style:italic;line-height:1.625;padding-left:1.375rem;">${takeaway.guidingQuestion}</p>
          </div>
        </div>
      </div>`;
    })
    .join("");

  return `
  <div class="pdf-page" style="min-height:768px;">
    <div class="report-page takeaways-bg" style="padding:2rem 4rem;overflow:hidden;height:100%;display:flex;flex-direction:column;position:relative;">
      <div style="position:absolute;top:-3.8%;left:0;right:0;bottom:0;pointer-events:none;">
        <div style="position:absolute;top:25%;right:25%;width:500px;height:500px;border-radius:9999px;background:radial-gradient(circle,hsla(42,90%,55%,0.06) 0%,transparent 60%);"></div>
        <div style="position:absolute;bottom:33%;left:25%;width:400px;height:400px;border-radius:9999px;background:radial-gradient(circle,hsla(190,95%,55%,0.04) 0%,transparent 60%);"></div>
        <div style="position:absolute;top:12%;left:0;width:33%;height:1px;background:linear-gradient(to right,hsla(42,90%,55%,0.2),transparent);"></div>
        <div style="position:absolute;top:12%;right:0;width:33%;height:1px;background:linear-gradient(to left,hsla(42,90%,55%,0.2),transparent);"></div>
      </div>

      <div style="position:relative;z-index:10;height:100%;display:flex;flex-direction:column;">
        <!-- Header -->
        <div style="text-align:center;margin-bottom:2rem;">
          <p style="font-size:1rem;letter-spacing:0.3em;text-transform:uppercase;color:hsl(42,90%,55%);margin-bottom:0.75rem;">${takeawaysPageConfig.sectionLabel}</p>
          <h2 style="font-size:2.28rem;line-height:2.5rem;font-weight:500;letter-spacing:0.05em;">
            <span style="background:linear-gradient(to right,hsl(200,20%,95%),hsl(200,20%,95%),hsl(210,20%,65%));-webkit-background-clip:text;-webkit-text-fill-color:transparent;">${takeawaysPageConfig.title}</span>
          </h2>
          <p style="color:hsl(210,20%,65%);margin-top:0.75rem;font-size:1rem;">${takeawaysPageConfig.subtitle}</p>
          <div style="width:6rem;height:2px;margin:1rem auto 0;background:linear-gradient(to right,transparent,hsla(42,90%,55%,0.6),transparent);"></div>
        </div>

        <!-- 2-col grid -->
        <div style="flex:1;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1.5rem;">
          ${cardsHTML}
        </div>
      </div>
    </div>
  </div>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

export async function generateReportHTML(
  apiData: ReportAPIData,                              // ✅ live API response
  staticData: LearnerReport,                           // ✅ static competency definitions
  checklistData: Record<string, CompetencyChecklistData>,
  takeawaysData: Record<string, CompetencyTakeaway>,
): Promise<string> {
  const runtimeCSS = collectAllCSS();

  // ✅ Use reportData.title to pick the right archetype image
  // const title = apiData.title || staticData.title;

  const overallScore =
  apiData?.overallScoreOutOf10 ?? staticData.overallScore;
// const title =
//   overallScore !== undefined
//     ? getTitleFromScore(overallScore)
//     : staticData.title;

const title =
  overallScore !== undefined
    ? getTitleFromScore(overallScore)
    : undefined;

  const [logoB64, archetypeB64] = await Promise.all([
    imageToBase64(leapLogo),
    imageToBase64(archetypeImgUrls[title] || archetypeImgUrls["Emerging People Leader"]),
  ]);

  const takeawayEntries = Object.entries(takeawaysData) as [string, CompetencyTakeaway][];

  const coverHTML = buildCoverPage(apiData.lenUsername, logoB64);
  const performanceHTML = buildPerformancePage(apiData, staticData, archetypeB64);
  const checklistHTML = buildChecklistPage(apiData, checklistData);
  const takeaways1HTML = buildTakeawayPage(takeawayEntries.slice(0, 2));   // page 4
  const takeaways2HTML = buildTakeawayPage(takeawayEntries.slice(2, 4));   // page 5

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=1366"/>
  <style>${runtimeCSS}</style>
  <style>${pdfStyles}</style>
</head>
<body style="font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;">
  <div class="pdf-mode bg-background">
    ${coverHTML}
    ${performanceHTML}
    ${checklistHTML}
    ${takeaways1HTML}
    ${takeaways2HTML}
  </div>
</body>
</html>`;
}
